import { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Loader2, CheckCircle2, XCircle, Upload, Trash2, Clock, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { HubAvatar } from "@/components/social/HubAvatar";
import {
  HUB_AVATAR_STATUS_LABEL,
  canChangeHandle,
  nextHandleChangeDate,
  normalizeHubDisplayName,
  validateHubDisplayName,
  validateHubHandle,
  hubHandleReservedExempt,
} from "@/lib/hub/hubIdentity";
import {
  useHubSettings,
  useHubHandleCheck,
  useRemoveHubAvatar,
  useUpdateHubSettings,
  useUploadHubAvatar,
} from "@/hooks/hub/useHubSettings";
import { useAuth } from "@/contexts/AuthContext";

/**
 * LR HUB SETTINGS — display name, @handle, role visibility, optional photo upload.
 * Writes ONLY to `hub_settings` + the private `hub-avatars` bucket.
 * Nothing here touches the core account, company or billing records.
 */

export function HubSettingsDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const { data: settings, isLoading } = useHubSettings();
  const update = useUpdateHubSettings();
  const upload = useUploadHubAvatar();
  const remove = useRemoveHubAvatar();
  const check = useHubHandleCheck();
  const fileRef = useRef<HTMLInputElement>(null);

  const [displayName, setDisplayName] = useState("");
  const [handle, setHandle] = useState("");
  const [showRole, setShowRole] = useState(true);
  const [available, setAvailable] = useState<boolean | null>(null);

  useEffect(() => {
    if (!settings) return;
    setDisplayName(settings.display_name);
    setHandle(settings.handle);
    setShowRole(settings.show_role);
    setAvailable(null);
  }, [settings, open]);

  const dirtyName =
    !!settings && normalizeHubDisplayName(displayName) !== settings.display_name;
  const dirtyHandle = !!settings && handle.trim().toLowerCase() !== settings.handle.toLowerCase();

  // Reserved-word bypass for LaneRunner admins / the founder (mirrors the DB guard).
  const { isAdmin, profile } = useAuth();
  const allowReserved = hubHandleReservedExempt(isAdmin, profile?.email);

  const nameError = useMemo(
    () => (dirtyName ? validateHubDisplayName(displayName) : null),
    [dirtyName, displayName],
  );
  const handleError = useMemo(
    () => (dirtyHandle && handle ? validateHubHandle(handle, allowReserved) : null),
    [dirtyHandle, handle, allowReserved],
  );
  const lockUntil = nextHandleChangeDate(settings?.handle_changed_at);
  const handleLocked = !!settings && !canChangeHandle(settings.handle_changed_at);

  useEffect(() => {
    setAvailable(null);
    if (!dirtyHandle || !handle || handleError) return;
    const t = setTimeout(() => {
      check.mutateAsync(handle).then(setAvailable).catch(() => setAvailable(null));
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handle, dirtyHandle, handleError]);

  const status = settings?.avatar_status ?? "initials";
  const shownPath = status === "approved" ? settings?.avatar_path ?? null : null;

  const canSave =
    !!settings &&
    !update.isPending &&
    ((dirtyName && !nameError) ||
      (dirtyHandle && !handleError && available === true && !handleLocked) ||
      showRole !== settings.show_role);

  const save = async () => {
    if (!settings) return;
    try {
      await update.mutateAsync({
        ...(dirtyName && !nameError ? { display_name: displayName } : {}),
        ...(dirtyHandle && !handleLocked && !handleError ? { handle } : {}),
        ...(showRole !== settings.show_role ? { show_role: showRole } : {}),
      });
      toast.success("LR Hub settings saved");
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Could not save your Hub settings.";
      // Lost a race for the handle — mark it unavailable so the field shows the conflict.
      if (/just taken/i.test(msg)) setAvailable(false);
      toast.error(msg);
    }

  };

  const pickFile = async (file?: File | null) => {
    if (!file) return;
    try {
      await upload.mutateAsync(file);
      toast.success("Photo submitted for review", {
        description: "You'll keep showing initials until a moderator approves it.",
      });
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Upload failed.");
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-base">LR Hub Settings</DialogTitle>
          <DialogDescription className="text-xs">
            Controls how you appear in LR Hub only. Your LaneRunner account, company and billing
            details are not affected.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex h-32 items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : !settings ? (
          <p className="py-6 text-center text-xs text-muted-foreground">
            Visit LR Hub once to create your Hub profile.
          </p>
        ) : (
          <div className="space-y-4">
            {/* Privacy tip — display only, no fields or behavior affected */}
            <div className="flex items-start gap-2.5 rounded-md border-l-4 border-l-emerald-600 bg-emerald-50 p-3">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-700" />
              <p className="text-xs leading-relaxed text-emerald-900">
                <span className="font-semibold">Want to stay private?</span>{" "}
                You don't have to use your real name or company. Pick a clean, professional
                username and display name to keep your identity private in the Hub.
              </p>
            </div>

            {/* Avatar */}

            <div className="flex items-start gap-3 rounded-lg border border-border p-3">
              <HubAvatar username={settings.display_name} avatarPath={shownPath} size="lg" />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-sm font-bold text-foreground">
                    {settings.display_name}{" "}
                    <span className="font-normal text-muted-foreground">@{settings.handle}</span>
                  </p>
                  <Badge
                    variant="outline"
                    className={
                      status === "approved"
                        ? "border-brand-teal-deep/40 text-[10px] text-brand-teal-deep"
                        : status === "pending"
                          ? "border-amber-500/40 text-[10px] text-amber-600"
                          : status === "rejected"
                            ? "border-destructive/40 text-[10px] text-destructive"
                            : "text-[10px] text-muted-foreground"
                    }
                  >
                    {status === "pending" ? <Clock className="mr-1 h-3 w-3" /> : null}
                    {HUB_AVATAR_STATUS_LABEL[status]}
                  </Badge>
                </div>
                <p className="mt-1 text-[11px] text-muted-foreground">
                  {status === "pending"
                    ? "Your photo is in the moderation queue. Members see your initials until it's approved."
                    : status === "rejected"
                      ? "Your last photo wasn't approved. You can upload a different one."
                      : "Optional: upload a photo (PNG/JPG/WEBP, max 4 MB). Reviewed before it goes public."}
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    className="hidden"
                    onChange={(e) => pickFile(e.target.files?.[0])}
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-[11px]"
                    disabled={upload.isPending}
                    onClick={() => fileRef.current?.click()}
                  >
                    {upload.isPending ? (
                      <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
                    ) : (
                      <Upload className="mr-1.5 h-3 w-3" />
                    )}
                    {status === "initials" ? "Upload photo" : "Replace photo"}
                  </Button>
                  {settings.avatar_path ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 text-[11px] text-destructive hover:text-destructive"
                      disabled={remove.isPending}
                      onClick={async () => {
                        try {
                          await remove.mutateAsync();
                          toast.success("Back to your generated avatar");
                        } catch (e: unknown) {
                          toast.error(e instanceof Error ? e.message : "Could not remove photo.");
                        }
                      }}
                    >
                      <Trash2 className="mr-1.5 h-3 w-3" />
                      Use initials
                    </Button>
                  ) : null}
                </div>
              </div>
            </div>

            {/* Display name */}
            <div className="space-y-1.5">
              <Label htmlFor="hub-settings-display-name" className="text-xs font-semibold uppercase tracking-wide">
                Display name
              </Label>
              <Input
                id="hub-settings-display-name"
                value={displayName}
                maxLength={30}
                autoComplete="off"
                onChange={(e) => setDisplayName(e.target.value)}
              />
              <p className="text-[11px] text-muted-foreground">
                {nameError ?? "Shown on your posts. Spaces and capitals allowed. Change it anytime."}
              </p>
            </div>

            {/* Handle */}
            <div className="space-y-1.5">
              <Label htmlFor="hub-settings-handle" className="text-xs font-semibold uppercase tracking-wide">
                Handle
              </Label>
              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                  @
                </span>
                <Input
                  id="hub-settings-handle"
                  value={handle}
                  disabled={handleLocked}
                  maxLength={20}
                  autoComplete="off"
                  onChange={(e) => setHandle(e.target.value.replace(/\s/g, "").toLowerCase())}
                  className="pl-7 pr-9"
                />
                <div className="absolute right-2.5 top-1/2 -translate-y-1/2">
                  {check.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  ) : dirtyHandle && available === true ? (
                    <CheckCircle2 className="h-4 w-4 text-brand-teal-deep" />
                  ) : dirtyHandle && (available === false || handleError) ? (
                    <XCircle className="h-4 w-4 text-destructive" />
                  ) : null}
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground">
                {handleLocked && lockUntil
                  ? `You can change your handle again on ${lockUntil.toLocaleDateString()}.`
                  : handleError
                    ? handleError
                    : dirtyHandle && available === false
                      ? "That handle is taken."
                      : "Letters, numbers, underscores. No spaces. Changeable once every 30 days."}
              </p>
            </div>

            {/* Role visibility */}
            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <div className="min-w-0 pr-3">
                <p className="text-xs font-semibold text-foreground">Show my role</p>
                <p className="text-[11px] text-muted-foreground">
                  Display Shipper / Broker / Carrier next to your name in the feed.
                </p>
              </div>
              <Switch checked={showRole} onCheckedChange={setShowRole} />
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
                Close
              </Button>
              <Button size="sm" onClick={save} disabled={!canSave}>
                {update.isPending ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : null}
                Save changes
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default HubSettingsDialog;
